import React, { useEffect, useState } from "react";
import { Form, Input, Select, Upload } from "antd";
import { ProductService } from "../../../services/productService";
import { toast } from "react-toastify";
import {
  Category,
  Label,
  ProductColor,
  Storage,
} from "../../../constants/Constants";
import SwitchComponent from "../../../components/SwitchComponent/SwitchComponent";

const { Option } = Select;

const AddProductForm = ({
  onAddProduct,
  onEditProduct,
  loading,
  initialValues,
  allProductList,
}) => {
  const [form] = Form.useForm();

  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedStorages, setSelectedStorages] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isActive, setIsActive] = useState(true);

  // 👇 Theo dõi category hiện tại
  const category = Form.useWatch("category", form);
  const isPhoneOrIpad = ["iphone", "ipad"].includes(
    String(category || "").toLowerCase()
  );

  useEffect(() => {
    if (initialValues) {
      setProductList(allProductList.filter((p) => p._id !== initialValues._id));
      const formattedColors = initialValues.colors.map((color) => ({
        ...color,
        images: initialValues.images
          .filter((img) => img.color === color.id)
          .map((image, index) => ({
            uid: image._id || index,
            name: image.src.split("/").pop(),
            url: image.src,
            status: "done",
          })),
      }));

      form.setFieldsValue({
        ...initialValues,
        colors: formattedColors.map((c) => c.id),
        storages: initialValues.storages.map((s) => s.size),
      });

      setSelectedColors(formattedColors);
      setSelectedStorages(initialValues.storages || []);
      setIsActive(initialValues.isActive);
    } else {
      form.resetFields();
      setProductList(allProductList);
      setSelectedColors([]);
      setSelectedStorages([]);
      setIsActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // 🧹 Khi đổi sang loại KHÁC iPhone/iPad -> xoá giá trị & state liên quan
  useEffect(() => {
    if (!isPhoneOrIpad) {
      form.setFieldsValue({
        storages: undefined,
        details: {
          screenSize: undefined,
          soc: undefined,
          coreCount: undefined,
          mainCamera: undefined,
          frontCamera: undefined,
          batteryCapacity: undefined,
        },
      });
      setSelectedStorages([]);
    }
  }, [isPhoneOrIpad, form]);

  const handleImageChange = (colorId, { fileList }) => {
    setSelectedColors((prev) =>
      prev.map((c) => (c.id === colorId ? { ...c, images: fileList } : c))
    );
  };

  const handleToggle = (newState) => setIsActive(newState);

  const handleSubmit = async (values) => {
    const allImages = selectedColors.flatMap((c) => c.images || []);
    if (allImages.length === 0) {
      toast.error("Vui lòng chọn ít nhất một ảnh!");
      return;
    }

    const newImages = allImages.filter((f) => f.originFileObj);
    try {
      let uploadedImageUrls = [];
      if (newImages.length > 0) {
        const formData = new FormData();
        newImages.forEach((f) => formData.append("image", f.originFileObj));
        const res = await ProductService.uploadProductImages(formData);
        if (!res.Data) {
          toast.error("Có lỗi khi tải ảnh!");
          return;
        }
        uploadedImageUrls = res.Data.imageUrls;
      }

      const finalImageUrls = allImages.map((f) =>
        f.url ? f.url : uploadedImageUrls.shift()
      );
      const imagesWithColors = selectedColors
        .filter((c) => Array.isArray(c.images))
        .flatMap((c) =>
          c.images.map(() => ({
            src: finalImageUrls.shift(),
            color: c.id,
          }))
        );

      // Nếu không phải iPhone/iPad, bỏ các field tech khỏi payload
      const details = isPhoneOrIpad ? values.details : {}; // hoặc undefined tuỳ backend

      const colorsWithoutImages = selectedColors.map(
        ({ images, ...rest }) => rest
      );

      const productData = {
        ...values,
        colors: colorsWithoutImages,
        storages: isPhoneOrIpad ? selectedStorages : [], // chỉ gửi khi iPhone/iPad
        details,
        images: imagesWithColors,
        isActive,
      };

      if (initialValues) {
        await onEditProduct(initialValues._id, productData);
        toast.success("Sản phẩm đã được cập nhật thành công!");
      } else {
        await onAddProduct(productData);
        toast.success("Sản phẩm đã được thêm thành công!");
      }

      form.resetFields();
      setSelectedColors([]);
      setSelectedStorages([]);
    } catch (e) {
      toast.error("Có lỗi xảy ra!");
      console.error(e);
    }
  };

  return (
    <div className="h-[30rem] overflow-auto" form-container="true">
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Tên & Loại */}
        <Form.Item
          label="Tên sản phẩm"
          name="name"
          rules={[{ required: true, message: "Tên sản phẩm là bắt buộc!" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Loại sản phẩm"
          name="category"
          rules={[{ required: true, message: "Vui lòng chọn loại sản phẩm!" }]}
        >
          <Select placeholder="Chọn loại sản phẩm">
            {Category.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Màu sắc — giữ nguyên cho mọi loại (nếu bạn muốn cũng có thể giới hạn theo loại) */}
        <Form.Item
          label="Màu sắc"
          name="colors"
          rules={[{ required: true, message: "Chọn màu sắc cho sản phẩm!" }]}
        >
          <Select
            mode="multiple"
            placeholder="Chọn màu sắc"
            onChange={(values) => {
              const newSelected = ProductColor.filter(
                (c) =>
                  values.includes(c.id) &&
                  !selectedColors.some((sc) => sc.id === c.id)
              ).map(({ id, available }) => ({ id, available }));

              const keep = selectedColors.filter((sc) =>
                values.includes(sc.id)
              );
              setSelectedColors([...keep, ...newSelected]);
            }}
          >
            {ProductColor.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedColors.length > 0 &&
          selectedColors.map(
            (color) =>
              color.available && (
                <div key={color.id}>
                  <Form.Item
                    label={
                      <div className="space-x-2">
                        Ảnh sản phẩm cho màu{" "}
                        <strong>
                          {ProductColor.find((m) => m.id === color.id)?.label ||
                            color.id}
                        </strong>
                      </div>
                    }
                  >
                    <Upload
                      name="file"
                      fileList={color.images}
                      beforeUpload={() => false}
                      onChange={(fileList) =>
                        handleImageChange(color.id, fileList)
                      }
                      accept={[".jpg", ".png", ".jpeg", ".webp"]}
                      listType="picture"
                    >
                      <button
                        type="button"
                        className="bg-gray-200 px-2 py-1 rounded-md"
                      >
                        Chọn ảnh
                      </button>
                    </Upload>
                  </Form.Item>
                </div>
              )
          )}

        {/* ====== CÁC THÔNG SỐ KỸ THUẬT: CHỈ iPhone/iPad ====== */}
        {isPhoneOrIpad && (
          <>
            <Form.Item
              label="Dung lượng bộ nhớ"
              name="storages"
              rules={[{ required: true, message: "Chọn dung lượng bộ nhớ!" }]}
            >
              <Select
                mode="multiple"
                placeholder="Chọn dung lượng bộ nhớ"
                onChange={(vals) => {
                  setSelectedStorages(
                    Storage.filter((s) => vals.includes(s.size))
                  );
                }}
              >
                {Storage.map((s) => (
                  <Option key={s.size} value={s.size}>
                    {s.size}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Màn hình"
              name={["details", "screenSize"]}
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập kích thước màn hình!",
                },
              ]}
            >
              <Input suffix="inch" />
            </Form.Item>

            <Form.Item
              label="SoC"
              name={["details", "soc"]}
              rules={[{ required: true, message: "Vui lòng nhập SoC!" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Số lượng Core"
              name={["details", "coreCount"]}
              rules={[
                { required: true, message: "Vui lòng nhập số lượng Core!" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Camera chính"
              name={["details", "mainCamera"]}
              rules={[
                { required: true, message: "Vui lòng nhập camera chính!" },
              ]}
            >
              <Input suffix="MP" />
            </Form.Item>

            <Form.Item
              label="Camera trước"
              name={["details", "frontCamera"]}
              rules={[
                { required: true, message: "Vui lòng nhập camera trước!" },
              ]}
            >
              <Input suffix="MP" />
            </Form.Item>

            {/* (Tuỳ chọn) Pin: cũng thường chỉ áp dụng cho phone/tablet */}
            <Form.Item
              label="Dung lượng pin"
              name={["details", "batteryCapacity"]}
              rules={[
                { required: true, message: "Vui lòng nhập dung lượng pin!" },
              ]}
            >
              <Input suffix="mAh" />
            </Form.Item>
          </>
        )}

        {/* Mô tả chi tiết — giữ bắt buộc cho mọi loại (tuỳ bạn) */}
        <Form.Item
          label="Mô tả chi tiết"
          name={["additionalDetails", "description"]}
          rules={[{ required: true, message: "Vui lòng nhập mô tả chi tiết!" }]}
        >
          <Input.TextArea />
        </Form.Item>

        {/* Nhãn / Liên quan / Kích hoạt */}
        <Form.Item label="Nhãn sản phẩm" name="labels">
          <Select mode="multiple" placeholder="Chọn nhãn">
            {Label.map((label) => (
              <Option key={label.id} value={label.id}>
                {label.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Sản phẩm liên quan" name="relatedProducts">
          <Select
            mode="multiple"
            showSearch
            placeholder="Tìm và chọn sản phẩm liên quan"
            optionFilterProp="children"
          >
            {productList.map((p) => (
              <Option key={p._id} value={p._id}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Hiển thị sản phẩm"
          name="isActive"
          valuePropName="checked"
          initialValue={true}
        >
          <SwitchComponent checked={isActive} onChange={handleToggle} />
        </Form.Item>

        <Form.Item>
          <button
            type="submit"
            className="text-white"
            style={{ width: "100%", backgroundColor: "black", height: "3rem" }}
            disabled={loading}
          >
            {!loading
              ? initialValues
                ? "Cập nhật sản phẩm"
                : "Thêm sản phẩm"
              : "Đang xử lý..."}
          </button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddProductForm;
